import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Observable, map, switchMap, of, catchError } from 'rxjs';
import { startWith, tap } from 'rxjs';
import { PostgridAutocompleteService } from 'src/app/postgrid-autocomplete.service';
import { PostgridCountryFilter, PostgridService } from '../../postgrid.service';
import { query } from '@angular/animations';
import * as Constants from './work-order-entry.constants'
//import { valueProperty } from 'ag-grid-enterprise/dist/lib/ag-charts-community/module-support';

@Component({

  selector: 'app-work-order-entry',
  templateUrl: './work-order-entry.component.html',
  styleUrls: ['./work-order-entry.component.scss']
})
export class WorkOrderEntryComponent implements OnInit {
  // test variables
  isVerifiedAddress: {[key: string]: boolean | null} = {
    'customer': null,
    'shipper': null,
    'consignee': null
  }

  // Customer address stuff
  customerAddressSuggestions: Address[] = [];
  suggestionQuery: string = '';
  customerGeocode: Geocode = {latitude: 0, longitude: 0};
  customerAddress: FormGroup = this.formBuilder.group({
    line1: '',
    line2: '',
    city: '',
    prov: '',
    postalCode: '',
    country: '',
    postgridVerified: [false, Validators.nullValidator],
    addressUuid: ''
  })
  customerVerifiedAddress: any;
  filteredCustomerAddressList: Observable<Address[]> | undefined;

  // Shipper address stuff
  shipperAddressSuggestions: Address[] = [];
  shipperGeocode: Geocode = {latitude: 0, longitude: 0};
  shipperAddress: FormGroup = this.formBuilder.group({
    line1: '',
    line2: '',
    city: '',
    prov: '',
    postalCode: '',
    country: '',
    postgridVerified: [false, Validators.nullValidator],
    addressUuid: ''
  })
  shipperVerifiedAddress: any;
  filteredShipperAddressList: Observable<Address[]> | undefined;

  // Consignee address stuff
  consigneeAddressSuggestions: Address[] = [];
  consigneeGeocode: Geocode = {latitude: 0, longitude: 0};
  consigneeAddress: FormGroup = this.formBuilder.group({
    line1: '',
    line2: '',
    city: '',
    prov: '',
    postalCode: '',
    country: '',
    postgridVerified: [false, Validators.nullValidator],
    addressUuid: ''
  })
  consigneeVerifiedAddress: any;
  filteredConsigneeAddressList: Observable<Address[]> | undefined;

  ratesErrorMessage = '';
  shipperAddressVerificationMsg = '';

  // Customer lookup
  customerList: any[] = [];
  filteredCustomerList: Observable<string[]> | undefined; // gets displayed in dropdown
  customerFullList: any[] | undefined; 

  // Shipper lookup
  shipperList: any[] = [];
  filteredShipperList: Observable<string[]> | undefined; // gets displayed in dropdown
  shipperFullList: any[] | undefined;

  // Consignee lookup
  consigneeList: any[] = [];
  filteredConsigneeList: Observable<string[]> | undefined; // gets displayed in dropdown
  consigneeFullList: any[] | undefined;


  // assing the steps for each section of the work order form
  step = 1
  totalSteps = 5

  // initialize forms for each section of the work order
  customerForm: FormGroup
  shipperForm: FormGroup
  consigneeForm: FormGroup
  defaultServiceChargeForm: FormGroup;
  loadForm: FormGroup
  serviceChargesShipper: FormGroup[] = [];
  serviceChargesConsignee: FormGroup[] = [];
  palletSets: FormGroup[] = []

  // strings for mat-selects. must be component properties to be accessible to html
  typeListRateMethod = Constants.TYPE_LIST_RATE_METHOD;
  typeListService = Constants.TYPE_LIST_SERVICE;
  typeListOrderScope = Constants.TYPE_LIST_ORDER_SCOPE;
  typeListEquipmentType = Constants.TYPE_LIST_EQUIPMENT_TYPE;
  typeListEquipmentOptions = Constants.TYPE_LIST_EQUIPMENT_OPTIONS;
  typeListPartialFull = Constants.TYPE_LIST_PARTIAL_FULL;
  typeListAccessorial = Constants.TYPE_LIST_ACCESSORIAL;
  typeListSurcharge = Constants.TYPE_LIST_SURCHARGE;
  typeListTradeshow = Constants.TYPE_LIST_TRADESHOW;
  typeListTrailerBuild = Constants.TYPE_LIST_TRAILER_BUILD;
  typeListCustoms = Constants.TYPE_LIST_CUSTOMS;
  typeListDelivery = Constants.TYPE_LIST_DELIVERY;
  typeListPickup = Constants.TYPE_LIST_PICKUP;
  typeListLabourer = Constants.TYPE_LIST_LABOURER;

  // fetch addresses associated with contact
  fetchContactAddresses(uuid: string, contact_type: string){
    let url = '/api/work_orders/contact_autofill.php';
    let body = {
      uuid: uuid,
      type: "contact_data"
    };

    this.http.get<any>(url, {params: body}).subscribe(
      (response) => {
        // customer
        if(contact_type == "customer"){
          this.customerAddressSuggestions = []; // clear suggestions list
          // store fetched addresses
          for(const address of response[0].addresses){
            let newAddress: Address = {
              address: address.address,
              address2: address.address_extended,
              city: address.locality,
              prov: address.region,
              pc: address.postal_code,
              country: address.country,
              postgridVerified: address.postgrid_verification,
              addressUuid: address.contact_address_uuid
            }
            this.customerAddressSuggestions.push(newAddress);
          }
        }
        // shipper
        else if(contact_type == "shipper"){
          this.shipperAddressSuggestions = []; // clear suggestions list
          // store fetched addresses
          for(const address of response[0].addresses){
            let newAddress: Address = {
              address: address.address,
              address2: address.address_extended,
              city: address.locality,
              prov: address.region,
              pc: address.postal_code,
              country: address.country,
              postgridVerified: address.postgrid_verification,
              addressUuid: address.contact_address_uuid
            }
            this.shipperAddressSuggestions.push(newAddress);
          }
        }
        // consignee
        else if(contact_type == "consignee"){
          this.consigneeAddressSuggestions = []; // clear suggestions list
          // store fetched addresses
          for(const address of response[0].addresses){
            let newAddress: Address = {
              address: address.address,
              address2: address.address_extended,
              city: address.locality,
              prov: address.region,
              pc: address.postal_code,
              country: address.country,
              postgridVerified: address.postgrid_verification,
              addressUuid: address.contact_address_uuid
            }
            this.consigneeAddressSuggestions.push(newAddress);
          }
        }
      },
      (error) => {
        console.error(`error fetching addresses for ${contact_type} from ams:`, error);
      }
    )

  }

  ngOnInit(){

    /* CUSTOMER AUTOCOMPLETE */
    this.customerForm.get('customer_company_name')?.valueChanges.subscribe((data) => {
      // when user has typed in 3 or more characters, get list of customers matching string
      if(data != null && data.length >= 3){
        this.getContactList('customer').subscribe((data) => this.customerList = data)
      }

      // find customer name within list of customers
      const index = this.customerFullList?.findIndex((item: any) => item.label === data);
      // if input matches a customer, set customer uuid and get list of addresses
      if(index !== -1 && this.customerFullList && index !== undefined){
        let customer_uuid = this.customerFullList[index]?.value;
        this.customerForm.get('customer_contact_uuid')?.setValue(customer_uuid);

        this.fetchContactAddresses(customer_uuid, 'customer');
      }
    })

    // filter customer list for display
    this.filteredCustomerList = this.customerForm.get('customer_company_name')?.valueChanges.pipe(
      startWith(''),
      map(customerList => customerList ? this.filterContactList(customerList, 'customer'): this.customerList.slice()),
    )

    this.filteredCustomerAddressList = this.customerAddress.get('line1')?.valueChanges.pipe(
      startWith(''),
      switchMap(query => query ? this.filterAddressList(query, 'customer') : of(this.customerAddressSuggestions.slice())),
      /* tap(filteredAddressList => console.log('Filtered address list:', filteredAddressList)) */
    )

    /* SHIPPER AUTOCOMPLETE */
    this.shipperForm.get('shipper_company_name')?.valueChanges.subscribe((data) => {
      if(data != null && data.length >= 3){
        this.getContactList('shipper').subscribe((data) => this.shipperList = data)
      }

      const shipper_index = this.shipperFullList?.findIndex((item: any) => item.label === data);
      if (shipper_index !== -1 && this.shipperFullList && shipper_index !== undefined){
        let shipper_uuid = this.shipperFullList[shipper_index]?.value;
        this.shipperForm.get('shipper_contact_uuid')?.setValue(shipper_uuid);

        this.fetchContactAddresses(shipper_uuid, 'shipper');
      }
    })

    this.filteredShipperList = this.shipperForm.get('shipper_company_name')?.valueChanges.pipe(
      startWith(''),
      map(shipperList => shipperList ? this.filterContactList(shipperList, 'shipper') : this.shipperList.slice())
    )

    this.filteredShipperAddressList = this.shipperAddress.get('line1')?.valueChanges.pipe(
      startWith(''),
      switchMap(query => query ? this.filterAddressList(query, 'shipper') : of(this.shipperAddressSuggestions.slice())),
      tap(filteredAddressList => console.log('Filtered address list:', filteredAddressList))
    );

    /* CONSIGNEE AUTOCOMPLETE */
    this.consigneeForm.get('consignee_company_name')?.valueChanges.subscribe((data) => {
      if(data != null && data.length >= 3){
        this.getContactList('consignee').subscribe((data) => this.consigneeList = data)
      }
      const consignee_index = this.consigneeFullList?.findIndex((item: any) => item.label === data);
      if (consignee_index !== -1 && this.consigneeFullList && consignee_index !== undefined){
        let consignee_uuid = this.consigneeFullList[consignee_index]?.value;
        this.consigneeForm.get('consignee_contact_uuid')?.setValue(consignee_uuid);

        this.fetchContactAddresses(consignee_uuid, 'consignee');
      }
    })
    this.filteredConsigneeList = this.consigneeForm.get('consignee_company_name')?.valueChanges.pipe(
      startWith(''),
      map(consigneeList => consigneeList ? this.filterContactList(consigneeList, 'consignee') : this.consigneeList.slice())
    )
    this.filteredConsigneeAddressList = this.consigneeAddress.get('line1')?.valueChanges.pipe(
      startWith(''),
      switchMap(query => query ? this.filterAddressList(query, 'consignee') : of(this.consigneeAddressSuggestions.slice()))
    );
  }

  // get lists of contacts for autofill
  getContactList(org_type: string): Observable<string[]>{
    // Set text to search for contact names depending on type of organization
    let searchText = (() => {
      switch(org_type){
        case 'customer': return this.customerForm.get('customer_company_name')?.value;
        case 'shipper': return this.shipperForm.get('shipper_company_name')?.value;
        case 'consignee': return this.consigneeForm.get('consignee_company_name')?.value;
        default: return '';
      }
    })();
    let url = `/api/work_orders/contact_autofill.php?type=${org_type}_contact_list&search=${searchText}`;

    // Get contact company names matching search text and populate appropriate lists
    return this.http.get<any[]>(url).pipe(
      map(response => {
        console.log(JSON.stringify(response))
        switch(org_type){
          case 'customer':
            if(response){
              this.customerFullList = response;
              this.customerList = response.map(item => item.label);
              return response.map(item => item.label);
            }
            else{
              console.log(`search text for customer name: ${searchText}`)
              this.customerList = [];
              return [];
            }
          case 'shipper':
            if(response){
              this.shipperFullList = response;
              this.shipperList = response.map(item => item.label);
              return response.map(item => item.label);
            }
            else{
              console.log(`search text for shipper name: ${searchText}`)
              this.shipperList = [];
              return [];
            }
          case 'consignee':
            if(response){
              this.consigneeFullList = response;
              this.consigneeList = response.map(item => item.label);
              return response.map(item => item.label);
            }
            else{
              console.log(`search text for consignee name: ${searchText}`)
              this.consigneeList = [];
              return [];
            }
          default:
            return [];
        }
      })
    )
  }

  // filter contact suggestions
  filterContactList(query: string, org_type: string){
    switch(org_type){
      case 'customer':
        return this.customerList.filter(customerList => 
          customerList.toLowerCase().indexOf(query.toLowerCase()) === 0)
      case 'shipper':
        return this.shipperList.filter(shipperList => 
          shipperList.toLowerCase().indexOf(query.toLowerCase()) === 0)
      case 'consignee':
        return this.consigneeList.filter(consigneeList => 
          consigneeList.toLowerCase().indexOf(query.toLowerCase()) === 0)
      default:
        return [];
    }
  }

  // filter addresses based on user query, then call postgrid if no matching addresses
  filterAddressList(query: string, org_type: string): Observable<Address[]>{
    let filteredList: Address[] = [];
    let countryQuery: string = '';
    // filter addresses
    switch(org_type){
      case 'customer':
        filteredList =  this.customerAddressSuggestions.filter(addressList => addressList.address.toLowerCase().indexOf(query.toLowerCase()) === 0);
        countryQuery = this.customerAddress.get('country')?.value.toUpperCase();
        break;
      case 'shipper':
        filteredList = this.shipperAddressSuggestions.filter(addressList => addressList.address.toLowerCase().indexOf(query.toLowerCase()) === 0)
        countryQuery = this.shipperAddress.get('country')?.value.toUpperCase();
        break;
      case 'consignee':
        filteredList = this.consigneeAddressSuggestions.filter(addressList => addressList.address.toLowerCase().indexOf(query.toLowerCase()) === 0)
        countryQuery = this.consigneeAddress.get('country')?.value.toUpperCase();
        break;
      default:
        filteredList = [];
    }

    // if there are any matching addresses, return it
    if(filteredList.length > 0){
      return of(filteredList)
    }
    // otherwise fetch addresses from postgrid
    if(query.length > 5){

      // set country filter, if specified
      let country: PostgridCountryFilter | undefined;
      if( 'CANADA'.includes(countryQuery) && countryQuery.length > 0){
        country = 'CA';
      }
      else if(('UNITED STATES'.includes(countryQuery) || 'USA'.includes(countryQuery) || 'U.S.A'.includes(countryQuery)) && countryQuery.length > 0){
        country = 'US';
      }

      return this.pgs.suggestAddress(query, country).pipe(
        map((suggestions) => suggestions.map((suggestion) => ({
          address: suggestion.line1,
          address2: suggestion.line2,
          city: suggestion.city,
          prov: suggestion.provinceOrState,
          pc: suggestion.postalOrZip,
          country: suggestion.country,
          postgridVerified: suggestion.postgridVerified
        }))),
        catchError(error => {
          console.error('Error fetching addresses from postgrid:', error);
          return of([]);
        })
      );
    }
    else{
      return of([]);
    }
  }

  // populates other parts of address form when an address is selected
  selectAddress(address: any, contact_type: string){
    let addressform;
    switch (contact_type){
      case 'customer': 
        addressform = this.customerAddress;
        break;
      case 'shipper':
        addressform = this.shipperAddress;
        break;
      case 'consignee':
        addressform = this.consigneeAddress;
        break;
      default:
        console.error('invalid contact type in click()');
        return;
    }

    addressform.get('address2')?.setValue(address.address2);
    addressform.get('city')?.setValue(address.city);
    addressform.get('prov')?.setValue(address.prov);
    addressform.get('postalCode')?.setValue(address.pc);
    addressform.get('country')?.setValue(address.country);
    addressform.get('postgridVerified')?.setValue(address.postgridVerified);
    addressform.get('addressUuid')?.setValue(address.addressUuid);

  }

  // check if addresses are verified
  checkAddresses(){
    // check if customer address is in our database
    if(this.customerAddress.get('addressUuid')?.value !== '' && this.customerAddress.get('addressUuid')?.value !== undefined){
      // if address is in database but not verified, verify and update adress in db
      if(!this.customerAddress.get('postgridVerified')?.value){
        this.updateAddress('customer');
      }
    }
    // if customer address not in database, assume it's from postgrid and add it to db
    else{
      this.addAddress('customer');
    }

    // shipper address
    let shipper_address_uuid = this.shipperAddress.get('addressUuid')?.value;
    if(this.shipperAddress.get('addressUuid')?.value !== '' && this.shipperAddress.get('addressUuid')?.value !== undefined){
      if(!this.shipperAddress.get('posgridVerified')?.value){
        this.updateAddress('shipper');
      }
    }
    else{
      this.addAddress('shipper');
    }

    // consignee address
    let consignee_address_uuid = this.consigneeAddress.get('addressUuid')?.value;
    if(this.consigneeAddress.get('addressUuid')?.value !== '' && this.consigneeAddress.get('addressUuid')?.value !== undefined){
      if(!this.consigneeAddress.get('posgridVerified')?.value){
        this.updateAddress('consignee');
      }
    }
    else{
      this.addAddress('consignee');
    }
  }

  // add postgrid address to database
  addAddress(contact_type: string){
    let url = '/api/contacts/contact_addresses.php';
    let addressForm: FormGroup;
    switch (contact_type){
      case 'customer': addressForm=this.customerAddress; break;
      case 'shipper': addressForm=this.shipperAddress; break;
      case 'consignee': addressForm=this.consigneeAddress; break;
      default:
        console.error(`address couldn't be added to database - invalid contact_type ${contact_type}`);
        return;
    }

    let postBody = {
      address_street: addressForm.get('line1')?.value,
      address_locality: addressForm.get('city')?.value,
      address_region: addressForm.get('prov')?.value,
      address_postal_code: addressForm.get('postalCode')?.value,
      address_country: addressForm.get('country')?.value,
      postgrid_verification: true,
      contact_uuid: this.customerForm.get('customer_contact_uuid')
    }

    this.http.post<any>(url, postBody).subscribe(
      (response => {
        console.log(response);
      })
    )
  }

  // verify and update address from database
  updateAddress(contact_type: string){
    let url = '/api/contacts/contact_addresses.php';
    let addressForm: FormGroup;
    let verifiedAddress;
    let geocode: Geocode;
    let uuid: string;
    switch (contact_type){
      case "customer":
        addressForm = this.customerAddress;
        verifiedAddress = this.customerVerifiedAddress;
        geocode = this.customerGeocode;
        uuid = this.customerAddress.get('addressUuid')?.value;
        break;
      case "shipper":
        addressForm = this.shipperAddress;
        verifiedAddress = this.shipperVerifiedAddress;
        geocode = this.shipperGeocode;
        uuid = this.shipperAddress.get('addressUuid')?.value;
        break;
      case "consignee":
        addressForm = this.consigneeAddress;
        verifiedAddress = this.consigneeVerifiedAddress;
        geocode = this.consigneeGeocode;
        uuid = this.consigneeAddress.get('addressUuid')?.value;
        break;
      default:
        console.error(`address cannot be updated in database; invalid contact_type=${contact_type}`)
        return;
    }

    // formatted address for postgrid verification input
    let input_address = {
      line1: addressForm.get('line1')?.value,
      line2: addressForm.get('line2')?.value,
      city: addressForm.get('city')?.value,
      provinceOrState: addressForm.get('prov')?.value,
      postalOrZip: addressForm.get('postalCode')?.value,
      country: addressForm.get('country')?.value
    }

    // verify address with postgrid
    this.pgs.verifyAddress(input_address).subscribe(
      (response)=> {
        verifiedAddress = response;
        if(response.geocode){
          geocode.latitude = response.geocode.latitude;
          geocode.longitude = response.geocode.longitude;
        }

        // update database
        let postBody = {
          address_street: response.line1,
          address_locality: response.city,
          address_region: response.provinceOrState,
          address_postal_code: response.postalOrZip,
          address_country: response.countryName,
          postgrid_verification: true
        }
        this.http.post<any>(`${url}?uuid=${uuid}`, postBody).subscribe(
          (response => {
            console.log(response);
          })
        )
      },
      (error) => {
        console.error('Error verifying address:', error);
      }
    )
  }

  ratesLookup(){
    // assemble the data
    const rate_method = this.customerForm.get('rate_method')?.value;
    const customer_contact_uuid = this.customerForm.get('customer_contact_uuid')?.value;
    const consignee_address_uuid = this.consigneeForm.get('consignee_address_uuid')?.value;
    const shipper_address_uuid = this.shipperForm.get('shipper_address_uuid')?.value;
    const delivery_type = this.loadForm.get('order_scope')?.value;

    if(rate_method == '' || customer_contact_uuid == '' || consignee_address_uuid == '' || shipper_address_uuid == '' || delivery_type == '')
      this.ratesErrorMessage = 'Unable to find rate. Please make sure all previous required information has been entered.';
    else
      this.ratesErrorMessage = '';

    // TO DO: pull rates data

  }


  constructor(private formBuilder: FormBuilder, private http: HttpClient, private pgs: PostgridService) {
    // build form for the customer section
    this.customerForm = this.formBuilder.group({
      customer_company_name: ['', Validators.required],
      customer_contact_uuid: ['', Validators.required],
      customer_address: [''],
      customer_address_uuid: [''],
      customer_shipping_bol: [''],
      customer_invoice_bol: '',
      customer_invoice_pod_required: '',
      customer_invoice_po_required: '',
      customer_rate_quote: '',
      customer_consignee_agreement: '',
      customer_at_pickup_notification: '',
      customer_delayed_pickup_notification: '',
      customer_at_delivery_notification: '',
      customer_delayed_delivery_notification: '',
      customer_assessorials_incurred_notification: '',
      customer_csa: '',
      customer_fast: '',
      customer_ace: '',
      customer_aci: '',
      customer_ct_pat: '',
      customer_canada_bond: '',
      customer_us_bond: '',
      customer_hazmat: '',
      customer_protect_from_freezing: '',
      customer_heated: '',
      customer_refrigeration: '',
      customer_food_grade: '',
      rate_method: ['', Validators.required],
      bill_to_company_name: ['', Validators.required],
      bill_to_address: ['', Validators.required],
    })

    // build form for the shipper section
    this.shipperForm = this.formBuilder.group({
      shipper_company_name: ['', Validators.required],
      shipper_contact_uuid: ['', Validators.required],
      shipper_address: ['', Validators.required],
      shipper_address_uuid: ['', Validators.required],
      shipper_loading_dock: '',
      shipper_pickup_tailgate: '',
      shipper_truck_space: '',
      shipper_pickup_straight_truck: '',
      shipper_bol_required: '',
      shipper_pickup_reference: '',
      estimated_date_shipper: '',
      estimated_time_start_shipper: '',
      shipper_is_residential: '',
      shipper_is_commercial: '',
      shipper_is_job_site: '',
      shipper_lift_lull: '',
      shipper_call_site: '',
      shipper_unmanned_job_site: '',
      shipper_job_site_contact_name: '',
      shipper_job_site_contact_phone: '',
      shipper_is_convention_center: '',
      shipper_is_pop_up: '',
      appointment_shipper: '',
      appointment_date_shipper: '',
      appointment_time_start_shipper: '',
      appointment_time_end_shipper: '',
    })

    // build form for the consignee section
    this.consigneeForm = this.formBuilder.group({
      consignee_company_name: ['', Validators.required],
      consignee_contact_uuid: ['', Validators.required],
      consignee_address: ['', Validators.required],
      consignee_address_uuid: ['', Validators.required],
      consignee_loading_dock: '',
      consignee_pickup_tailgate: '',
      consignee_truck_space: '',
      consignee_pickup_straight_truck: '',
      consignee_bol_required: '',
      consignee_delivery_reference: '',
      estimated_date_consignee: '',
      estimated_time_start_consignee: '',
      consignee_is_residential: '',
      consignee_is_commercial: '',
      consignee_is_job_site: '',
      consignee_lift_lull: '',
      consignee_call_site: '',
      consignee_unmanned_job_site: '',
      consignee_job_site_contact_name: '',
      consignee_is_convention_center: '',
      consignee_is_pop_up: '',
      appointment_consignee: '',
      appointment_date_consignee: '',
      appointment_time_start_consignee: '',
      appointment_time_end_consignee: ''
    })

    // build form for the default service section
    this.defaultServiceChargeForm = this.formBuilder.group({
      shipper_service_charge_service: ['', Validators.required],
      shipper_service_charge_service_type: ['', Validators.required],
    })

    // build form for the load
    this.loadForm = this.formBuilder.group({
      order_scope: ['', Validators.required],
      order_scope_secondary: ['', Validators.required],
      operations_notation: ['', Validators.required],
      num_pallets: '',
      length: '',
      width: '',
      height: '',
      weight: ''
    })

    // build form for the pallet sets
    const palletSetFormGroup = this.formBuilder.group({
      num_pallets: '',
      length: '',
      width: '',
      height: '',
      weight: ''
    });

    this.palletSets.push(palletSetFormGroup);

  }

  // Pushes new service charge form to the group of service charges
  addServiceCharge(form: string) {
    const serviceChargeFormGroup = this.formBuilder.group({
      service: ['', Validators.required],
      service_type: ['', Validators.required],
    });

    if (form === 'shipper') {
      this.serviceChargesShipper.push(serviceChargeFormGroup);
    } else {
      this.serviceChargesConsignee.push(serviceChargeFormGroup);
    }
  }

  // Deletes the service charge at specific index when user clicks remove service charge
  removeServiceCharge(index: number, form: string) {
    if (form === 'shipper') {
      this.serviceChargesShipper.splice(index, 1);
    } else {
      this.serviceChargesConsignee.splice(index, 1);
    }
  }

  // Function to return which country has been selected to determine which typeList array to use (province or states)
  getService(service: string): string[] {
    const serviceMapping: {[key: string]: any}= {
      'Accessorial Service': this.typeListAccessorial,
      'Service Program': this.typeListService,
      'Surcharge Service': this.typeListSurcharge,
      'Tradeshow Service': this.typeListTradeshow,
      'Trailer Build Service': this.typeListTrailerBuild,
      'Customs Service': this.typeListCustoms,
      'Delivery Service': this.typeListDelivery,
      'Pickup Service': this.typeListPickup,
      'Labourer Service': this.typeListLabourer
    }
    return (service in serviceMapping) ? serviceMapping[service] : [];
  }

  // Event handler for changing the address country and setting appropriate province/state to the address_region in the form
  onServiceChange(index: number, form: string) {
    let serviceFormGroup: FormGroup | undefined
    if (form === 'shipper') {
      serviceFormGroup = this.serviceChargesShipper.at(index);
    } else {
      serviceFormGroup = this.serviceChargesConsignee.at(index);
    }
    const serviceControl = serviceFormGroup?.get('service');
    const serviceTypeControl = serviceFormGroup?.get('service_type');
    const serviceTypes = this.getService(serviceControl?.value);
    serviceTypeControl?.setValue(null);
    serviceTypeControl?.setValidators(serviceTypes.length > 0 ? Validators.required : null);
    serviceTypeControl?.updateValueAndValidity();
  }

  // Pushes new pallet set form to the group of pallet sets
  addPalletSet() {
    const palletSetFormGroup = this.formBuilder.group({
      num_pallets: '',
      length: '',
      width: '',
      height: '',
      weight: ''
    });

    this.palletSets.push(palletSetFormGroup);
  }

  // Deletes the pallet set at specific index when user clicks remove pallet sets
  removePalletSet(index: number) {

    this.palletSets.splice(index, 1);

  }

  // Proceed to the next step in the form
  nextStep() {
    if (this.step < this.totalSteps) {
      this.step++;
    }
  }

  // Move back to the previous step in the form
  prevStep() {
    if (this.step > 1) {
      this.step--;
    }
  }

  // Change step to the step relative to the icon on the progress bar clicked on
  changeStep(newStep: number) {
    this.step = newStep;
    if(newStep == 5)
      this.ratesLookup();
  }

  // NOT IN USE YET
  submit(){
    // for addresses not previously verified but already in database, change postgridVerified status
    // for new addresses, add to database
  }

  
}

// key names matches response format in postgrid completions endpoint
interface Address{
  address: string;
  address2?: string;
  city: string;
  prov: string;
  pc: string;
  country: string;
  postgridVerified?: boolean | null,
  addressUuid?: string | null
}

interface Geocode{
  latitude: number;
  longitude: number;
}
